import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Dialog,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import { Globe2 } from "lucide-react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  createCountryDocumentAction,
  updateCountryDocumentAction,
} from "../../Actions/CountryDocumentActions";

import { listCountriesAction } from "../../Actions/CountriesActions";
import { listDocumentTypesAction } from "../../Actions/DocumentTypeActions";
import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import "../../components/Common/FormModal.css";

const getChangedPlanFields = (oldData, newData) => {
  const changes = {};
  if (!oldData) return changes;

  Object.keys(newData).forEach((key) => {
    const oldVal = oldData[key];
    const newVal = newData[key];

    const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
    const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

    if (oldIsEmpty && newIsEmpty) return;

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = {
        old: oldVal,
        new: newVal,
      };
    }
  });

  return changes;
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      borderRadius: "12px",
      border: "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
      mt: 0.5,
      fontFamily: "var(--app-font)",
    },
  },
};

const AddEditCountryDocument = ({
  open,
  handleClose,
  editData,
  setSnackbar,
  refresh,
}) => {
  const [countries, setCountries] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [changedFields, setChangedFields] = useState({});

  const formik = useFormik({
    initialValues: {
      country_id: editData?.country?.id || "",
      document_type_id: editData?.documentType?.id || "",
      is_supported: editData?.is_supported ?? true,
      is_double_sided: editData?.is_double_sided ?? false,
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      country_id: yup.string().required("Country is required").nullable(),
      document_type_id: yup
        .string()
        .required("Document type is required")
        .nullable(),
    }),
    onSubmit: async (values) => {
      setLoading(true);

      try {
        let response;
        const payload = { ...values };

        if (editData) {
          const updatedChangedFields = { ...changedFields };

          if (updatedChangedFields.country_id) {
            const oldCountry = countries.find(
              (c) =>
                String(c.id) === String(updatedChangedFields.country_id.old)
            );
            const newCountry = countries.find(
              (c) =>
                String(c.id) === String(updatedChangedFields.country_id.new)
            );

            updatedChangedFields.country = {
              old: oldCountry?.name || "",
              new: newCountry?.name || "",
            };
            delete updatedChangedFields.country_id;
          }

          if (updatedChangedFields.document_type_id) {
            const oldDocumentType = documentTypes.find(
              (d) =>
                String(d.id) ===
                String(updatedChangedFields.document_type_id.old)
            );
            const newDocumentType = documentTypes.find(
              (d) =>
                String(d.id) ===
                String(updatedChangedFields.document_type_id.new)
            );

            updatedChangedFields.document_type = {
              old: oldDocumentType?.name || "",
              new: newDocumentType?.name || "",
            };
            delete updatedChangedFields.document_type_id;
          }

          payload.changedFields = JSON.stringify(
            Object.keys(updatedChangedFields)
          );
          payload.comparisonData = JSON.stringify(updatedChangedFields);

          response = await updateCountryDocumentAction(editData.id, payload);
        } else {
          response = await createCountryDocumentAction(payload);
        }

        setSnackbar({
          open: true,
          message: response?.data?.message || response?.message || "Success",
          severity: "success",
        });

        handleClose();
        refresh();
      } catch (err) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.message || "Error",
          severity: "error",
        });
      }

      setLoading(false);
    },
  });

  useEffect(() => {
    listCountriesAction()
      .then((countryRes) => {
        setCountries(countryRes?.data?.data?.rows || []);
      })
      .catch((err) => {
        console.error("Country fetch error", err);
      });

    listDocumentTypesAction()
      .then((docTypeRes) => {
        setDocumentTypes(docTypeRes?.data?.data || []);
      })
      .catch((err) => {
        console.error("Document types fetch error", err);
      });
  }, []);

  useEffect(() => {
    if (editData && countries.length && documentTypes.length) {
      const initialValuesToSet = {
        country_id: editData?.country?.id || "",
        document_type_id: editData?.documentType?.id || "",
        is_supported: editData?.is_supported ?? true,
        is_double_sided: editData?.is_double_sided ?? false,
      };
      formik.setValues(initialValuesToSet);
      setInitialFormValues(initialValuesToSet);
    }
  }, [editData, countries, documentTypes]);

  useEffect(() => {
    if (!editData || !initialFormValues) return;

    const changed = getChangedPlanFields(initialFormValues, formik.values);
    setChangedFields(changed);
  }, [formik.values, initialFormValues, editData]);

  const isEdit = Boolean(editData);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "fm-dialog" }}
    >
      <div className="fm-header">
        <span className="fm-header-icon" aria-hidden>
          <Globe2 size={20} />
        </span>
        <div className="fm-header-copy">
          <h2>{isEdit ? "Update KYC Country Document" : "Add KYC Country Document"}</h2>
          <p>
            {isEdit
              ? "Change which document type applies to this country and its options."
              : "Link a document type to a country and set its verification options."}
          </p>
        </div>
        <IconButton
          aria-label="close"
          className="fm-close"
          onClick={handleClose}
        >
          <MyCloseIcon />
        </IconButton>
      </div>

      <form onSubmit={formik.handleSubmit} className="fm-form">
        <div className="fm-body">
          <section className="fm-section">
            <div className="fm-section-head">
              <h3>Mapping</h3>
              <p>Country and the document type accepted for KYC there.</p>
            </div>
            <div className="fm-card">
              <div className="fm-grid is-stack">
                <div className="fm-field">
                  <label id="country-label">
                    Country<span className="fm-req">*</span>
                  </label>
                  <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(
                      formik.touched.country_id && formik.errors.country_id
                    )}
                  >
                    <Select
                      name="country_id"
                      value={formik.values.country_id}
                      displayEmpty
                      onChange={(e) =>
                        formik.setFieldValue("country_id", e.target.value)
                      }
                      onBlur={formik.handleBlur}
                      MenuProps={selectMenuProps}
                      inputProps={{ "aria-labelledby": "country-label" }}
                    >
                      <MenuItem value="" disabled>
                        Select country
                      </MenuItem>
                      {countries?.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {formik.touched.country_id && formik.errors.country_id}
                    </FormHelperText>
                  </FormControl>
                </div>

                <div className="fm-field">
                  <label id="document-type-label">
                    Document type<span className="fm-req">*</span>
                  </label>
                  <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(
                      formik.touched.document_type_id &&
                        formik.errors.document_type_id
                    )}
                  >
                    <Select
                      name="document_type_id"
                      value={formik.values.document_type_id}
                      displayEmpty
                      onChange={(e) =>
                        formik.setFieldValue("document_type_id", e.target.value)
                      }
                      onBlur={formik.handleBlur}
                      MenuProps={selectMenuProps}
                      inputProps={{ "aria-labelledby": "document-type-label" }}
                    >
                      <MenuItem value="" disabled>
                        Select document type
                      </MenuItem>
                      {documentTypes?.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {formik.touched.document_type_id &&
                        formik.errors.document_type_id}
                    </FormHelperText>
                  </FormControl>
                </div>
              </div>
            </div>
          </section>

          <section className="fm-section">
            <div className="fm-section-head">
              <h3>Options</h3>
              <p>Verification behaviour for this country and document pair.</p>
            </div>
            <div className="fm-card">
              <div className="fm-toggle-list">
                <label
                  className={`fm-toggle${
                    formik.values.is_supported ? " is-on" : ""
                  }`}
                >
                  <span>
                    <p className="fm-toggle-title">Supported</p>
                    <p className="fm-toggle-sub">
                      Allow this document type for the selected country.
                    </p>
                  </span>
                  <Switch
                    name="is_supported"
                    checked={formik.values.is_supported}
                    onChange={formik.handleChange}
                    inputProps={{ "aria-label": "Supported" }}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#22419e",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#22419e",
                      },
                    }}
                  />
                </label>

                <label
                  className={`fm-toggle${
                    formik.values.is_double_sided ? " is-on" : ""
                  }`}
                >
                  <span>
                    <p className="fm-toggle-title">Double sided</p>
                    <p className="fm-toggle-sub">
                      Require front and back images during verification.
                    </p>
                  </span>
                  <Switch
                    name="is_double_sided"
                    checked={formik.values.is_double_sided}
                    onChange={formik.handleChange}
                    inputProps={{ "aria-label": "Double sided" }}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#22419e",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#22419e",
                      },
                    }}
                  />
                </label>
              </div>
            </div>
          </section>
        </div>

        <div className="fm-footer">
          <button type="button" className="fm-btn" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="fm-btn is-primary"
            disabled={loading}
          >
            {isEdit ? "Update" : "Add"}
            {loading && <CircularProgress size={16} color="inherit" />}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default AddEditCountryDocument;
